import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ModalLayout from '../layouts/ModalLayout'
import VehicleDetail from './VehicleDetailModal'
import { modalPrimaryButtonStyle } from '../styles/buttonStyles'
import TableSortLabel from '@mui/material/TableSortLabel'
import ResetTableButton from '../components/ResetTableButton'
import ExportButton from '../components/ExportButton'
import GenericSearchToggle from '../components/GenericSearchToggle'
import useGenericTextSearch from '../hooks/useGenericTextSearch'
import useSharedModalUiState from '../hooks/useSharedModalUiState'
import { useAuth } from '../context/AuthContext'
import {
  exportVehiclesCSV,
  exportVehiclesXLS,
  exportVehiclesPDF,
  exportVehiclesXML,
  exportVehiclesJSON,
  exportVehiclesICS
} from '../utils/vehicleExport'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const vehicleTooltipFields = [
  ['name', 'vehicle_name'],
  ['licensePlate', 'vehicle_license_plate'],
  ['usageType', 'vehicle_usage_type'], 
  ['vin', 'vehicle_vin'],
  ['brand', 'vehicle_brand'],
  ['model', 'vehicle_model'],
  ['variantType', 'vehicle_type'],
  ['vehicleType', 'vehicle_class'],
  ['category', 'vehicle_category'],
  ['fuelType', 'vehicle_fuel_type'],
  ['engineCapacity', 'vehicle_engine_capacity'],
  ['power', 'vehicle_power'],
  ['transmission', 'vehicle_transmission'],
  ['color', 'vehicle_color'],
  ['emissionsCo2', 'vehicle_emissions_co2'],
  ['consumption', 'vehicle_consumption'],
  ['emissionClass', 'vehicle_emission_class'],
  ['year', 'vehicle_year'],
  ['stkDate', 'vehicle_stk_date'],
  ['ekDate', 'vehicle_ek_date'],
  ['serviceDate', 'vehicle_service_date'],
  ['tiresAllSeason', 'vehicle_tires_all_season'],
  ['startDate', 'vehicle_start_date'],
  ['pzpDate', 'vehicle_pzp_date'],
  ['pzpFrequency', 'vehicle_pzp_frequency'],
  ['cascoDate', 'vehicle_casco_date'],
  ['cascoFrequency', 'vehicle_casco_frequency'],
  ['registrationCountry', 'vehicle_registration_country'],
  ['note', 'vehicle_note'],
]

const vehicleDetailFields = [
  ['name', 'vehicle_name'],
  ['licensePlate', 'vehicle_license_plate'],
  ['vin', 'vehicle_vin'],
  ['brand', 'vehicle_brand'],
  ['model', 'vehicle_model'],
  ['variantType', 'vehicle_type'],
  ['vehicleType', 'vehicle_class'],
  ['category', 'vehicle_category'],
  ['fuelType', 'vehicle_fuel_type'],
  ['engineCapacity', 'vehicle_engine_capacity'],
  ['power', 'vehicle_power'],
  ['transmission', 'vehicle_transmission'],
  ['color', 'vehicle_color'],
  ['emissionsCo2', 'vehicle_emissions_co2'],
  ['consumption', 'vehicle_consumption'],
  ['emissionClass', 'vehicle_emission_class'],
  ['year', 'vehicle_year'],
  ['stkDate', 'vehicle_stk_date'],
  ['ekDate', 'vehicle_ek_date'],
  ['serviceDate', 'vehicle_service_date'],
  ['tiresAllSeason', 'vehicle_tires_all_season'],
  ['startDate', 'vehicle_start_date'],
  ['pzpDate', 'vehicle_pzp_date'],
  ['pzpFrequency', 'vehicle_pzp_frequency'],
  ['cascoDate', 'vehicle_casco_date'],
  ['cascoFrequency', 'vehicle_casco_frequency'],
  ['registrationCountry', 'vehicle_registration_country'],
  ['usageType', 'vehicle_usage_type'], 
  ['note', 'vehicle_note'],
]

const VEHICLE_MODAL_COLUMNS_KEY = 'vehicleModalColumns'
const VEHICLE_MODAL_COL_WIDTHS_KEY = 'vehicleModalColWidths'

function SortableHeaderCell({ col, orderBy, order, onSort, colWidths, onResize }) {
  const { t } = useTranslation()

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: col.key })
  const customWidth = colWidths[col.key]

  return (
    <th
      ref={setNodeRef}
      style={{
        ...thStyle,
        transform: CSS.Transform.toString(transform),
        transition,
        ...(customWidth ? { width: customWidth, minWidth: customWidth, maxWidth: customWidth } : { minWidth: 120 }),
        position: 'relative',
        paddingRight: 8,
        boxSizing: 'border-box'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span {...attributes} {...listeners} style={{ cursor: 'grab', marginRight: 6, color: '#999' }}>⠿</span>
        <TableSortLabel
          active={orderBy === col.key}
          direction={orderBy === col.key ? order : 'asc'}
          onClick={() => onSort(col.key)}
        >
          {t(col.labelKey)}
        </TableSortLabel>
      </div>
      <div style={{ position: 'absolute', right: 4, top: '20%', bottom: '20%', width: 1, background: '#9e9e9e' }} />
      <div onMouseDown={(e) => onResize(e, col.key)} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'col-resize' }} />
    </th>
  )
}

const VehicleListModal = ({
  open,
  onClose,
  vehicles = [],
  vehiclesLoading,
  vehiclesError,
  openExportDropdown
}) => {
  const { t } = useTranslation()
const { user } = useAuth()
const isLoggedIn = Boolean(user)

const membershipId = user?.membership_type_id ?? null
const limitedMembership = membershipId === 1 || membershipId === 4
const fullMembership = !limitedMembership

  const [viewport, setViewport] = useState(window.innerWidth)
  const [modalSize, setModalSize] = useState('fullscreen')
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [vehicleDetailOpen, setVehicleDetailOpen] = useState(false)
  const [hoveredVehicle, setHoveredVehicle] = useState(null)
  const [activeRow, setActiveRow] = useState(null)
  const [tooltipEnabled, setTooltipEnabled] = useState(() => {
    const stored = localStorage.getItem('tooltipEnabled')
    return stored === null ? true : stored === 'true'
  })

  useEffect(() => {
    localStorage.setItem('tooltipEnabled', tooltipEnabled)
  }, [tooltipEnabled])

  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)
  const { showSearch, setShowSearch } = useSharedModalUiState()
  const [searchQuery, setSearchQuery] = useState('')
const [forceOpenExport, setForceOpenExport] = useState(false)

useEffect(() => {
  if (openExportDropdown) {
    setForceOpenExport(true)

    const timer = setTimeout(() => {
      setForceOpenExport(false)
    }, 50)

    return () => clearTimeout(timer)
  }
}, [openExportDropdown])
  const [orderBy, setOrderBy] = useState(() => localStorage.getItem('vehicleOrderBy') || 'name')
  const [order, setOrder] = useState(() => localStorage.getItem('vehicleOrder') || 'asc')
const [columns, setColumns] = useState(() => {
  const stored = localStorage.getItem(VEHICLE_MODAL_COLUMNS_KEY)
  if (stored) return JSON.parse(stored)

  return vehicleDetailFields.map(([key, labelKey]) => ({
    key,
    labelKey
  }))
})
  const [colWidths, setColWidths] = useState(() => {
    const stored = localStorage.getItem(VEHICLE_MODAL_COL_WIDTHS_KEY)
    return stored ? JSON.parse(stored) : {}
  })
  const sensors = useSensors(useSensor(PointerSensor))

  useEffect(() => {
    localStorage.setItem(VEHICLE_MODAL_COLUMNS_KEY, JSON.stringify(columns))
  }, [columns])

  useEffect(() => {
    localStorage.setItem(VEHICLE_MODAL_COL_WIDTHS_KEY, JSON.stringify(colWidths))
  }, [colWidths])

  useEffect(() => {
    localStorage.setItem('vehicleOrderBy', orderBy)
  }, [orderBy])

  useEffect(() => {
    localStorage.setItem('vehicleOrder', order)
  }, [order])

  useEffect(() => {
    const handleResize = () => setViewport(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (open) setModalSize('fullscreen')
  }, [open])

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setOrderBy(col)
      setOrder('asc')
    }
  }

  const handleMouseDown = (e, key) => {
    const startX = e.clientX
    const startWidth = colWidths[key] || 150

    const onMouseMove = (event) => {
      const delta = event.clientX - startX
      const newWidth = Math.max(80, startWidth + delta)
      setColWidths(prev => ({ ...prev, [key]: newWidth }))
    }

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = columns.findIndex(c => c.key === active.id)
    const newIndex = columns.findIndex(c => c.key === over.id)
    const reordered = [...columns]
    const [moved] = reordered.splice(oldIndex, 1)
    reordered.splice(newIndex, 0, moved)
    setColumns(reordered)
  }

  const formatVehicleValue = (vehicle, key) => {
    if (['stkDate', 'ekDate', 'serviceDate', 'startDate', 'pzpDate', 'cascoDate'].includes(key)) {
      return vehicle[key] ? new Date(vehicle[key]).toLocaleDateString('sk-SK') : '-'
    }

    if (key === 'pzpFrequency' || key === 'cascoFrequency') {
      const freq = Number(vehicle[key])
      if (freq === 1) return t('vehicle_payment_yearly')
      if (freq === 2) return t('vehicle_payment_halfyear')
      if (freq === 4) return t('vehicle_payment_quarterly')
      return vehicle[key] || '-'
    }

    if (key === 'tiresAllSeason') {
      return vehicle[key] === true ? t('vehicle_allseason') : '-'
    }

    return vehicle[key] || '-'
  }

  const sortedVehicles = [...vehicles].sort((a, b) => {
    let aVal = formatVehicleValue(a, orderBy)
    let bVal = formatVehicleValue(b, orderBy)

    if (['engineCapacity', 'power', 'emissionsCo2', 'consumption', 'year'].includes(orderBy)) {
      aVal = Number(a[orderBy]) || 0
      bVal = Number(b[orderBy]) || 0
    }

    if (typeof aVal === 'string') aVal = aVal.toLowerCase()
    if (typeof bVal === 'string') bVal = bVal.toLowerCase()

    if (aVal > bVal) return order === 'asc' ? 1 : -1
    if (aVal < bVal) return order === 'asc' ? -1 : 1
    return 0
  })

  const visibleVehicles = useGenericTextSearch(
    sortedVehicles,
    showSearch ? searchQuery : '',
    {
      formatter: (vehicle) => ({
        ...vehicle,
        searchStkDate: vehicle?.stkDate ? new Date(vehicle.stkDate).toLocaleDateString('sk-SK') : '',
        searchEkDate: vehicle?.ekDate ? new Date(vehicle.ekDate).toLocaleDateString('sk-SK') : '',
        searchServiceDate: vehicle?.serviceDate ? new Date(vehicle.serviceDate).toLocaleDateString('sk-SK') : '',
        searchStartDate: vehicle?.startDate ? new Date(vehicle.startDate).toLocaleDateString('sk-SK') : '',
        searchPzpDate: vehicle?.pzpDate ? new Date(vehicle.pzpDate).toLocaleDateString('sk-SK') : '',
        searchCascoDate: vehicle?.cascoDate ? new Date(vehicle.cascoDate).toLocaleDateString('sk-SK') : ''
      })
    }
  )
const exportRecords = vehicles.map(v => ({ ...v }))
const exportHeaders = columns.map(c => c.key)

const selectedVehicleIndex = visibleVehicles.findIndex(
  (item) => item?.id === selectedVehicle?.id
)

const goToPrevVehicle = () => {
  if (selectedVehicleIndex <= 0) return
  const prevVehicle = visibleVehicles[selectedVehicleIndex - 1]
  setSelectedVehicle(prevVehicle)
  setActiveRow(prevVehicle?.id ?? null)
}

const goToNextVehicle = () => {
  if (selectedVehicleIndex < 0 || selectedVehicleIndex >= visibleVehicles.length - 1) return
  const nextVehicle = visibleVehicles[selectedVehicleIndex + 1]
  setSelectedVehicle(nextVehicle)
  setActiveRow(nextVehicle?.id ?? null)
}

  if (!open) return null

  return (
    <>
      <ModalLayout open={open} onClose={onClose} size={modalSize} closeOnEscape={!vehicleDetailOpen}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>

          <div
            style={{
              position: 'absolute',
              top: -10,
              right: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <GenericSearchToggle
              visible={showSearch}
              query={searchQuery}
              onQueryChange={setSearchQuery}
              onToggle={() => {
                setShowSearch(prev => {
                  if (prev) setSearchQuery('')
                  return !prev
                })
              }}
              placeholder={t('TRIPLOG.SEARCH_PLACEHOLDER', { defaultValue: 'Hľadať...' })}
            />

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                cursor: 'pointer',
                fontSize: 13,
                userSelect: 'none',
		marginTop: 5
              }}
            >
              <input
                type="checkbox"
                checked={tooltipEnabled}
                onChange={(e) => setTooltipEnabled(e.target.checked)}
                style={{
                  cursor: 'pointer',
                  accentColor: '#06857a',
                  appearance: 'none',
                  width: 16,
                  height: 16,
                  border: '1px solid #1e3f44',
                  borderRadius: 3,
                  background: tooltipEnabled ? '#06857a' : '#fff'
                }}
              />
              {t('vehicle_preview_toggle')}
            </label>
<ResetTableButton
  isModified={
    Object.keys(colWidths).length > 0 ||
    JSON.stringify(columns.map(c => c.key)) !==
      JSON.stringify(vehicleDetailFields.map(([key]) => key)) ||
    orderBy !== 'name' ||
    order !== 'asc'
  }
  onReset={() => {
const resetCols = vehicleDetailFields.map(([key, labelKey]) => ({
  key,
  labelKey
}))

    setColumns(resetCols)
    setColWidths({})
    setOrderBy('name')
    setOrder('asc')

    localStorage.removeItem(VEHICLE_MODAL_COLUMNS_KEY)
    localStorage.removeItem(VEHICLE_MODAL_COL_WIDTHS_KEY)
    localStorage.removeItem('vehicleOrderBy')
    localStorage.removeItem('vehicleOrder')
  }}
/>
<ExportButton
  onCSV={() => exportVehiclesCSV(exportRecords, exportHeaders, t)}
  onXLS={() => exportVehiclesXLS(exportRecords, exportHeaders, t)}
  onPDF={() => exportVehiclesPDF(exportRecords, exportHeaders, t)}
  onXML={() => exportVehiclesXML(exportRecords, exportHeaders)}
  onJSON={() => exportVehiclesJSON(exportRecords)}
  onICS={() => exportVehiclesICS(exportRecords)}
  forceOpen={forceOpenExport}
  isLoggedIn={isLoggedIn}
  canUsePremiumFormats={fullMembership}
/>
            <button
              onClick={() =>
                setModalSize(prev => prev === 'fullscreen' ? 'max' : 'fullscreen')
              }
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: 32
              }}
            >
              ⛶
            </button>
          </div>

          <h3 style={{ fontSize: 20, marginTop: 0, marginBottom: 10 }}>
            {t('vehicle_list_title')}
          </h3>

          {vehiclesLoading && <div>{t('LOADING')}</div>}
          {vehiclesError && <div style={{ color: 'red' }}>{vehiclesError}</div>}
<div
  style={{
    border: '1px solid #c7c7c7',
    borderRadius: 10,
    background: '#fff',
    flex: 1,
    overflow: 'auto',
    minHeight: 0
  }}
>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={columns.map(c => c.key)} strategy={horizontalListSortingStrategy}>
                <table
                  style={{
                    borderCollapse: 'collapse',
                    width: 'max-content',
                    minWidth: '100%',
                    fontSize: 12,
                    whiteSpace: 'nowrap'
                  }}
                >
                  <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
                    <tr style={{ background: '#c8e1e1', fontWeight: 700 }}>
                      {columns.map((col) => (
                        <SortableHeaderCell
                          key={col.key}
                          col={col}
                          orderBy={orderBy}
                          order={order}
                          onSort={handleSort}
                          colWidths={colWidths}
                          onResize={handleMouseDown}
                        />
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {visibleVehicles.length === 0 ? (
                      <tr>
                        <td colSpan={vehicleDetailFields.length + 1} style={{ padding: 16 }}>
                          {t('vehicle_empty')}
                        </td>
                      </tr>
                    ) : (
                      visibleVehicles.map(v => (
                        <tr
                      key={v.id}
                      style={{
                        borderTop: '1px solid #ececec',
                        backgroundColor:
                          activeRow === v.id
                            ? '#e6f4f1'
                            : hoveredVehicle?.id === v.id
                              ? '#f3fafa'
                              : '#fff',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease'
                      }}
                      onClick={() => {
                        setActiveRow(v.id)
                        setSelectedVehicle(v)
                        setVehicleDetailOpen(true)
                      }}
                      onMouseEnter={(e) => {
                        if (e.target.closest('.vehicle-detail-button')) return
                        setHoveredVehicle(v)
                      }}
                      onMouseMove={(e) => {
                        if (e.target.closest('.vehicle-detail-button')) {
                          setHoveredVehicle(null)
                        } else {
                          setHoveredVehicle(v)
                        }
                      }}
                      onMouseLeave={(e) => {
                        const related = e.relatedTarget
                        if (related && related.closest && related.closest('.vehicle-tooltip')) return
                        setHoveredVehicle(null)
                      }}
                    >
{vehicleDetailFields.map(([key]) => (
  <td key={key} style={tdStyle}>
    {['stkDate','ekDate','serviceDate','startDate','pzpDate','cascoDate'].includes(key)
      ? (v[key]
          ? new Date(v[key]).toLocaleDateString('sk-SK')
          : '-')
      : (key === 'pzpFrequency' || key === 'cascoFrequency')
        ? (() => {
            const freq = Number(v[key])
            if (freq === 1) return t('vehicle_payment_yearly')
            if (freq === 2) return t('vehicle_payment_halfyear')
            if (freq === 4) return t('vehicle_payment_quarterly')
            return v[key] || '-'
          })()
: key === 'usageType'
  ? (v[key] === 'firemne'
      ? t('vehicle_usage_type_company')
      : v[key] === 'sukromne'
        ? t('vehicle_usage_type_private')
        : '-')
: key === 'tiresAllSeason' || key === 'hasCasco'
  ? (v[key] ? t('dropdown_yes') : t('dropdown_no'))
  : v[key] || '-'}
  </td>
))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
</div>

          {tooltipEnabled && hoveredVehicle && (
            <div
              className="vehicle-tooltip"
              onMouseLeave={() => setHoveredVehicle(null)}
              style={{
                position: 'fixed',
                right:
                  viewport >= 2400 ? 69 :
                  viewport >= 1920 ? 51 :
                  viewport >= 1600 ? 60 :
                  viewport >= 1366 ? 40 : 20,
                bottom:
                  viewport >= 1920 ? 120 :
                  viewport >= 1600 ? 90 :
                  viewport >= 1366 ? 10 : 30,
                fontSize:
                  viewport >= 1920 ? 14 :
                  viewport >= 1600 ? 14 :
                  viewport >= 1366 ? 13 : 13,
                maxHeight:
                  viewport >= 1920 ? '80vh' :
                  viewport >= 1600 ? '75vh' : '96vh',
                background: '#f3fafa',
                border: '1px solid #1e3f44',
                borderRadius: 8,
                padding: '16px 20px',
                boxShadow: '0 8px 20px rgba(0,0,0,0.35)',
                zIndex: 9999,
                overflowY: 'auto'
              }}
            >
              {vehicleTooltipFields.map(([key, labelKey]) => (
                <div key={key} style={{ marginBottom: 6, display: 'flex' }}>
                  <strong style={{ minWidth: 300 }}>
                    {t(labelKey)}:
                  </strong>
<span style={{ color: '#1e3f44', fontWeight: 600 }}>
  {['stkDate','ekDate','serviceDate','startDate','pzpDate','cascoDate'].includes(key)
    ? (hoveredVehicle[key]
        ? new Date(hoveredVehicle[key]).toLocaleDateString('sk-SK')
        : '-')
    : (key === 'pzpFrequency' || key === 'cascoFrequency')
      ? (() => {
          const freq = Number(hoveredVehicle[key])
          if (freq === 1) return t('vehicle_payment_yearly')
          if (freq === 2) return t('vehicle_payment_halfyear')
          if (freq === 4) return t('vehicle_payment_quarterly')
          return hoveredVehicle[key] || '-'
        })()
: key === 'usageType'
  ? (hoveredVehicle[key] === 'firemne'
      ? t('vehicle_usage_type_company')
      : hoveredVehicle[key] === 'sukromne'
        ? t('vehicle_usage_type_private')
        : '-')
: key === 'tiresAllSeason' || key === 'hasCasco'
  ? (hoveredVehicle[key] ? t('dropdown_yes') : t('dropdown_no'))
  : hoveredVehicle[key] || '-'}
</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 15, display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={onClose}
              onMouseEnter={() => setHoverBtn('close')}
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() => setPressedBtn('close')}
              onMouseUp={() => setPressedBtn(null)}
              style={modalPrimaryButtonStyle(
                pressedBtn,
                'close',
                hoverBtn === 'close'
              )}
            >
              {t('CLOSE')}
            </button>
          </div>

        </div>
      </ModalLayout>

<VehicleDetail
  open={vehicleDetailOpen}
  onClose={() => {
    setVehicleDetailOpen(false)
    setSelectedVehicle(null)
    setActiveRow(null)
  }}
  vehicle={selectedVehicle}
  onPrevVehicle={goToPrevVehicle}
  onNextVehicle={goToNextVehicle}
  hasPrevVehicle={selectedVehicleIndex > 0}
  hasNextVehicle={selectedVehicleIndex >= 0 && selectedVehicleIndex < visibleVehicles.length - 1}
/>
    </>
  )
}

const thStyle = {
  padding: '8px 12px',
  textAlign: 'left',
  borderBottom: '1px solid #c7c7c7'
}

const tdStyle = {
  padding: '8px 12px'
}

export default VehicleListModal